import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Slider } from "@/components/ui/slider";
import { Users, Plus, MapPin, Route, ChevronUp, Clock, Search, Map, Flame, Navigation, ChevronRight } from "lucide-react";
import { ClubCard } from "@/components/clubs/ClubCard";
import { FadeInDirection } from "@/components/trip/FadeInDirection";
import { AppHeader } from "@/components/AppHeader";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";
import { PortugalMap } from "@/components/PortugalMap";
import { NotificationLoginPopup } from "@/components/NotificationLoginPopup";
import { SEO } from "@/components/SEO";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import { useAuth } from "@/contexts/AuthContext";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { getUploadUrl } from "@/lib/utils";
import { useMostActiveClubs, useRegionalMapsSettings, useClubSearch } from "@/hooks/queries";
import { useSliderSettings } from "@/hooks/queries/useSettings";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Country centers for map positioning
const COUNTRY_CENTERS = {
  PT: [39.4, -8.2], ES: [40.4, -3.7], FR: [46.6, 2.3], DE: [51.2, 10.4],
  IT: [42.5, 12.5], GB: [54.0, -2.5], NL: [52.3, 4.9], BE: [50.8, 4.4],
  default: [48.0, 10.0],
};

// Haversine formula
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Map recenter component
function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 6);
  }, [center, map]);
  return null;
}

// Map click handler
function SearchLocationMarker({ position, setPosition, radius }) {
  useMapEvents({
    click(e) { setPosition([e.latlng.lat, e.latlng.lng]); },
  });

  return position ? (
    <>
      <Marker position={position} />
      <Circle center={position} radius={radius * 1000} pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.1, weight: 2 }} />
    </>
  ) : null;
}

export default function ClubsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const { isAuthenticated, user } = useAuth();
  
  // React Query hooks
  const { data: clubsData, isLoading: clubsLoading } = useMostActiveClubs(100);
  const { data: sliderSettingsData } = useSliderSettings();
  const { data: regionalMapsSettings } = useRegionalMapsSettings();
  
  // Derive settings with defaults
  const sliderSettings = sliderSettingsData || {
    clubs_slider: { min_km: 5, max_km: 50, default_km: 20 },
    clubs_nearby: { radius_km: 20 }
  };
  
  // Club search hook
  const { 
    data: searchResults, 
    isLoading: searchQueryLoading,
    search: executeSearch,
    reset: resetSearch,
    searchParams: currentSearchParams
  } = useClubSearch();
  
  // Derive sorted clubs from query data
  const allGroups = useMemo(() => clubsData || [], [clubsData]);
  const sortedByRecent = useMemo(() => 
    [...allGroups].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [allGroups]
  );
  
  // Local UI state
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState(null);
  const [activeFilter, setActiveFilter] = useState({ type: 'recent' });
  
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [locationChoiceModalOpen, setLocationChoiceModalOpen] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  const userCoords = user?.coordinates;
  const userCountry = user?.country || "PT";
  const userMapCenter = userCoords ? [userCoords.lat, userCoords.lng] : (COUNTRY_CENTERS[userCountry] || COUNTRY_CENTERS.default);
  
  const [searchMapPosition, setSearchMapPosition] = useState(null);
  const [searchMapCenter, setSearchMapCenter] = useState(userMapCenter);
  const [searchRadius, setSearchRadius] = useState(sliderSettings?.clubs_slider?.default_km || 20);
  const [searchName, setSearchName] = useState("");
  const [searchPositionSource, setSearchPositionSource] = useState(null);
  const [searchGettingLocation, setSearchGettingLocation] = useState(false);
  const [searchLocationName, setSearchLocationName] = useState(null);
  
  // Derive search loading state from React Query
  const searchLoading = searchQueryLoading;
  
  const GROUPS_PER_PAGE = 8;
  const [currentPage, setCurrentPage] = useState(1);
  
  // Combined loading state
  const loading = clubsLoading;
  
  const displayGroups = filteredGroups !== null ? filteredGroups : groups;
  const totalPages = Math.ceil(displayGroups.length / GROUPS_PER_PAGE);
  
  const paginatedGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * GROUPS_PER_PAGE;
    return displayGroups.slice(startIndex, startIndex + GROUPS_PER_PAGE);
  }, [displayGroups, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredGroups, activeFilter]);

  // Initialize groups when data loads
  useEffect(() => {
    if (sortedByRecent.length > 0 && groups.length === 0) {
      setGroups(sortedByRecent);
      setFilteredGroups(sortedByRecent);
      setActiveFilter({ type: 'recent' });
    }
  }, [sortedByRecent, groups.length]);
  
  // Update search radius when settings load
  useEffect(() => {
    if (sliderSettingsData?.clubs_slider?.default_km) {
      setSearchRadius(sliderSettingsData.clubs_slider.default_km);
    }
  }, [sliderSettingsData]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const clearFilters = () => {
    setFilteredGroups(null);
    setActiveFilter({ type: 'recent' });
    const sorted = [...allGroups].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    setGroups(sorted);
    sessionStorage.removeItem('grupos_filter_state');
  };

  // Filter handlers
  const handleRecentFilter = () => {
    const sorted = [...allGroups].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    setGroups(sorted);
    setFilteredGroups(null);
    setActiveFilter({ type: 'recent' });
    sessionStorage.setItem('grupos_filter_state', JSON.stringify({ type: 'recent' }));
  };

  const handleActiveFilter = () => {
    const sorted = [...allGroups].sort((a, b) => (b.trip_count || 0) - (a.trip_count || 0));
    setGroups(sorted);
    setFilteredGroups(null);
    setActiveFilter({ type: 'active' });
    sessionStorage.setItem('grupos_filter_state', JSON.stringify({ type: 'active' }));
  };

  // Nearby filter
  const filterGroupsByCoords = (lat, lng, source, locationName = null) => {
    const RADIUS_KM = sliderSettings?.clubs_nearby?.radius_km || 20;
    const nearbyGroups = allGroups
      .filter(g => {
        if (!g.location_coords?.lat || !g.location_coords?.lng) return false;
        return calculateDistance(lat, lng, g.location_coords.lat, g.location_coords.lng) <= RADIUS_KM;
      })
      .map(g => ({
        ...g,
        distance: calculateDistance(lat, lng, g.location_coords.lat, g.location_coords.lng)
      }))
      .sort((a, b) => a.distance - b.distance);
    
    setFilteredGroups(nearbyGroups);
    setActiveFilter({ type: 'nearby', source, radius: RADIUS_KM, count: nearbyGroups.length, locationName });
    setLocationChoiceModalOpen(false);
    sessionStorage.setItem('grupos_filter_state', JSON.stringify({ type: 'nearby', lat, lng, source, radius: RADIUS_KM, locationName }));
  };

  const handleLocationChoice = (choice) => {
    if (choice === 'current') {
      if (navigator.geolocation) {
        setGettingLocation(true);
        setLocationChoiceModalOpen(false);
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            let locationName = 'Posição atual';
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
                { headers: { 'Accept-Language': 'pt' } }
              );
              if (response.ok) {
                const data = await response.json();
                const address = data.address;
                const locality = address.city || address.town || address.village || address.municipality || address.county;
                const district = address.state || address.district;
                if (locality && district) locationName = `GPS: ${locality}, ${district}`;
                else if (locality) locationName = `GPS: ${locality}`;
                else if (district) locationName = `GPS: ${district}`;
              }
            } catch (e) {
              // Geocoding failed, use default name
            }
            setGettingLocation(false);
            filterGroupsByCoords(lat, lng, 'gps', locationName);
          },
          () => {
            setGettingLocation(false);
            setFilteredGroups([]);
            setActiveFilter({ type: 'nearby', noCoords: true, gpsError: true });
          },
          { timeout: 10000, maximumAge: 60000 }
        );
      }
    } else if (choice === 'profile' && user?.coordinates?.lat) {
      const loc = user?.concelho ? `Perfil: ${user.concelho}, ${user.distrito}` : `Perfil: ${user?.distrito || ''}`;
      filterGroupsByCoords(user.coordinates.lat, user.coordinates.lng, 'profile', loc);
    }
  };

  // Map filter
  const handleMapSelect = (distrito) => {
    if (!distrito) return;
    const filtered = allGroups.filter(g => g.distrito?.toLowerCase() === distrito.toLowerCase());
    setFilteredGroups(filtered);
    setActiveFilter({ type: 'map', distrito });
    setMapModalOpen(false);
    sessionStorage.setItem('grupos_filter_state', JSON.stringify({ type: 'map', distrito }));
  };

  // Search handler - uses React Query hook
  const handleSearch = () => {
    if (!isAuthenticated) {
      handleUnauthorizedAccess(navigate, { message: t("clubs.register_to_search") });
      return;
    }
    if (!searchMapPosition) return;
    
    const trimmedName = searchName.trim().substring(0, 20);
    
    // Execute search via React Query hook
    executeSearch({
      lat: searchMapPosition[0],
      lng: searchMapPosition[1],
      radius: searchRadius,
      searchName: trimmedName || null
    });
    
    // Update UI state
    setActiveFilter({ 
      type: 'search', 
      isMapSearch: true, 
      hasPosition: true, 
      locationName: searchLocationName, 
      radius: searchRadius, 
      searchName: trimmedName || null 
    });
    setSearchModalOpen(false);
    sessionStorage.setItem('grupos_filter_state', JSON.stringify({ 
      type: 'search', 
      lat: searchMapPosition[0], 
      lng: searchMapPosition[1], 
      radius: searchRadius, 
      searchName: trimmedName || null 
    }));
  };
  
  // Update filteredGroups when search results change
  useEffect(() => {
    if (searchResults && currentSearchParams) {
      setFilteredGroups(searchResults);
      setActiveFilter(prev => ({ ...prev, count: searchResults.length }));
    }
  }, [searchResults, currentSearchParams]);

  const handleSearchPositionSource = async (source) => {
    if (source === 'profile' && userCoords) {
      setSearchMapPosition([userCoords.lat, userCoords.lng]);
      setSearchMapCenter([userCoords.lat, userCoords.lng]);
      setSearchPositionSource('profile');
      const loc = user?.concelho ? `${user.concelho}, ${user.distrito}` : user?.distrito || '';
      setSearchLocationName(loc ? `Perfil: ${loc}` : 'Perfil');
    } else if (source === 'gps') {
      if (navigator.geolocation) {
        setSearchGettingLocation(true);
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            setSearchMapPosition([lat, lng]);
            setSearchMapCenter([lat, lng]);
            setSearchPositionSource('gps');
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
                { headers: { 'Accept-Language': 'pt' } }
              );
              if (response.ok) {
                const data = await response.json();
                const address = data.address;
                const locality = address.city || address.town || address.village || address.municipality || address.county;
                const district = address.state || address.district;
                if (locality && district) setSearchLocationName(`GPS: ${locality}, ${district}`);
                else if (locality) setSearchLocationName(`GPS: ${locality}`);
                else if (district) setSearchLocationName(`GPS: ${district}`);
                else setSearchLocationName('GPS: Posição atual');
              }
            } catch (e) {
              setSearchLocationName('GPS: Posição atual');
            }
            setSearchGettingLocation(false);
          },
          () => {
            setSearchGettingLocation(false);
            alert('Não foi possível obter a localização GPS.');
          },
          { timeout: 10000, maximumAge: 60000 }
        );
      }
    }
  };

  const resetSearchForm = () => {
    const center = userCoords ? [userCoords.lat, userCoords.lng] : (COUNTRY_CENTERS[userCountry] || COUNTRY_CENTERS.default);
    setSearchMapPosition(userCoords ? [userCoords.lat, userCoords.lng] : null);
    setSearchMapCenter(center);
    setSearchRadius(sliderSettings?.clubs_slider?.default_km || 20);
    setSearchName("");
    setSearchPositionSource(userCoords ? 'profile' : null);
    const loc = user?.concelho ? `${user.concelho}, ${user.distrito}` : user?.distrito || '';
    setSearchLocationName(userCoords ? (loc ? `Perfil: ${loc}` : 'Perfil') : null);
    resetSearch(); // Also reset the query
  };

  useEffect(() => {
    if (searchModalOpen && userCoords && !searchMapPosition) {
      setSearchMapPosition([userCoords.lat, userCoords.lng]);
      setSearchMapCenter([userCoords.lat, userCoords.lng]);
      setSearchPositionSource('profile');
      const loc = user?.concelho ? `${user.concelho}, ${user.distrito}` : user?.distrito || '';
      setSearchLocationName(loc ? `Perfil: ${loc}` : 'Perfil');
    }
  }, [searchModalOpen, userCoords]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-36 hero-background flex flex-col" data-testid="groups-page">
      <SEO
        title={t("clubs_section.seo_title")}
        description={t("clubs.seo_description")}
        path="/clubes"
      />
      <AppHeader />

      <PageHeader
        actionBarButtons={[
          {
            icon: Plus,
            label: t("clubs.actions.create"),
            onClick: () => isAuthenticated ? navigate("/meus-clubes?tab=create") : handleUnauthorizedAccess(navigate),
            tooltip: t("clubs.actions.create_tooltip"),
            testId: "new-group-btn"
          }
        ]}
        iconType="icon"
        icon={Users}
        overline={t("header.clubs")}
        title={t("clubs_section.title_explore")}
      />

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 relative z-10 flex-1 scroll-mt-24" id="topo">
        <div className="rounded-lg bg-black/60 backdrop-blur-sm border border-white/10 px-4 py-3 mb-6">
          <p className="text-sm text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" data-testid="clubs-intro">{t("clubs.intro")}</p>
        </div>
        <section id="clubes-section" className="scroll-mt-32">
          {allGroups.length === 0 ? (
            <Card className="bg-card border-border rounded-xl" data-testid="empty-state">
              <CardContent className="p-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t("clubs.empty_state.no_clubs")}</h3>
                <p className="text-muted-foreground">{t("clubs.empty_state.no_clubs_description")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Filter Bar */}
              <TooltipProvider delayDuration={200}>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Recent */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={activeFilter.type === 'recent' ? "default" : "outline"}
                        size="sm"
                        onClick={handleRecentFilter}
                        className={`gap-2 justify-center ${activeFilter.type === 'recent' ? '' : 'bg-card/95 backdrop-blur-sm border-border hover:border-primary/50'}`}
                      >
                        <Clock className="h-4 w-4" />
                        {t("clubs.most_recent")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>{t("clubs.tooltip_recent")}</p></TooltipContent>
                  </Tooltip>

                  {/* Nearby */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={activeFilter.type === 'nearby' ? "default" : "outline"}
                        size="sm"
                        disabled={gettingLocation}
                        onClick={() => {
                          if (!isAuthenticated) {
                            handleUnauthorizedAccess(navigate, { message: t("clubs.register_to_search") });
                            return;
                          }
                          setLocationChoiceModalOpen(true);
                        }}
                        className={`gap-2 justify-center ${activeFilter.type === 'nearby' ? '' : 'bg-card/95 backdrop-blur-sm border-border hover:border-primary/50'}`}
                      >
                        {gettingLocation ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <Navigation className="h-4 w-4" />
                        )}
                        {t("clubs.nearby")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>{t("clubs.tooltip_nearby")}</p></TooltipContent>
                  </Tooltip>

                  {/* Active */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={activeFilter.type === 'active' ? "default" : "outline"}
                        size="sm"
                        onClick={handleActiveFilter}
                        className={`gap-2 justify-center ${activeFilter.type === 'active' ? '' : 'bg-card/95 backdrop-blur-sm border-border hover:border-primary/50'}`}
                      >
                        <Flame className="h-4 w-4" />
                        {t("clubs.active")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>{t("clubs.tooltip_active")}</p></TooltipContent>
                  </Tooltip>

                  {/* Search */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={activeFilter.type === 'search' ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (!isAuthenticated) {
                            handleUnauthorizedAccess(navigate, { message: t("clubs.register_to_search") });
                            return;
                          }
                          setSearchModalOpen(true);
                        }}
                        className={`gap-2 justify-center ${activeFilter.type === 'search' ? '' : 'bg-card/95 backdrop-blur-sm border-border hover:border-primary/50'}`}
                      >
                        <Search className="h-4 w-4" />
                        {t("clubs.search.button")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>{t("clubs.tooltip_search")}</p></TooltipContent>
                  </Tooltip>

                  {/* Map */}
                  {regionalMapsSettings?.PT?.enabled && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={activeFilter.type === 'map' ? "default" : "outline"}
                          size="sm"
                          onClick={() => setMapModalOpen(true)}
                          className={`gap-2 justify-center ${activeFilter.type === 'map' ? '' : 'bg-card/95 backdrop-blur-sm border-border hover:border-primary/50'}`}
                        >
                          <Map className="h-4 w-4" />
                          {t("clubs.map")}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom"><p>{t("clubs.tooltip_map", { country: "Portugal" })}</p></TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TooltipProvider>

              {/* Active Filters Display */}
              {(activeFilter.type !== 'recent' && activeFilter.type !== 'active') && (
                <div className="flex flex-wrap items-center gap-2">
                  {activeFilter.type === 'nearby' && !activeFilter.noCoords && (
                    <>
                      <span className="px-2 py-0.5 rounded text-[11px] bg-card border border-border text-foreground flex items-center gap-1">
                        <Navigation className="h-3 w-3" />
                        {activeFilter.locationName || 'Localização'}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[11px] bg-card border border-border text-foreground">{activeFilter.radius} km</span>
                      <span className="px-2 py-0.5 rounded text-[11px] bg-primary text-primary-foreground font-medium">{activeFilter.count} {activeFilter.count === 1 ? 'clube' : 'clubes'}</span>
                      <button onClick={clearFilters} className="w-5 h-5 rounded flex items-center justify-center text-xs bg-card border border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors">✕</button>
                    </>
                  )}
                  {activeFilter.type === 'map' && (
                    <>
                      <span className="px-2 py-0.5 rounded text-[11px] bg-card border border-border text-foreground flex items-center gap-1">
                        <Map className="h-3 w-3" />
                        {activeFilter.distrito}
                      </span>
                      <button onClick={clearFilters} className="w-5 h-5 rounded flex items-center justify-center text-xs bg-card border border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors">✕</button>
                    </>
                  )}
                  {activeFilter.type === 'search' && (
                    <>
                      <span className="px-2 py-0.5 rounded text-[11px] bg-card border border-border text-foreground flex items-center gap-1">
                        <Search className="h-3 w-3" />
                        {activeFilter.locationName || 'Pesquisa'}
                      </span>
                      {activeFilter.searchName && (
                        <span className="px-2 py-0.5 rounded text-[11px] bg-card border border-border text-foreground">&quot;{activeFilter.searchName}&quot;</span>
                      )}
                      <span className="px-2 py-0.5 rounded text-[11px] bg-primary text-primary-foreground font-medium">{activeFilter.count} {activeFilter.count === 1 ? 'clube' : 'clubes'}</span>
                      <button onClick={clearFilters} className="w-5 h-5 rounded flex items-center justify-center text-xs bg-card border border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors">✕</button>
                    </>
                  )}
                </div>
              )}

              {/* Groups Grid */}
              {displayGroups.length === 0 ? (
                <Card className="bg-card/80 backdrop-blur-sm border-border rounded-xl">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <Users className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold">{t("clubs.empty_state.no_clubs_found")}</h3>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Top Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-end mb-4">
                      <Pagination 
                        currentPage={currentPage} 
                        totalPages={totalPages} 
                        onPageChange={setCurrentPage}
                        isAuthenticated={isAuthenticated}
                        scrollToId="topo"
                      />
                    </div>
                  )}

                  {/* Group Cards Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {paginatedGroups.map((club, idx) => (
                      <FadeInDirection
                        key={club.club_id}
                        direction={idx % 2 === 0 ? "left" : "right"}
                        delay={idx * 0.08}
                      >
                        <ClubCard club={club} currentLang={currentLang} />
                      </FadeInDirection>
                    ))}
                  </div>
                </>
              )}

              {/* Bottom Pagination with Top button */}
              {totalPages > 1 && (
                <div className="flex items-center justify-end gap-4 mt-6">
                  <Pagination 
                    currentPage={currentPage} 
                    totalPages={totalPages} 
                    onPageChange={setCurrentPage}
                    isAuthenticated={isAuthenticated}
                    scrollToId="topo"
                  />
                  <Button variant="outline" size="sm" onClick={scrollToTop} className="gap-2 bg-card/90 backdrop-blur-sm border-primary/50 hover:bg-primary hover:text-primary-foreground h-9" data-testid="scroll-to-top-btn">
                    <ChevronUp className="h-4 w-4" />
                    {t("actions.top")}
                  </Button>
                </div>
              )}

              {totalPages <= 1 && (
                <div className="flex justify-end pt-6">
                  <Button variant="outline" size="sm" onClick={scrollToTop} className="gap-2 bg-card/90 backdrop-blur-sm border-primary/50 hover:bg-primary hover:text-primary-foreground" data-testid="scroll-to-top-single-btn">
                    <ChevronUp className="h-4 w-4" />
                    {t("actions.top")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      <Footer />

      {/* Location Choice Modal */}
      <Dialog open={locationChoiceModalOpen} onOpenChange={setLocationChoiceModalOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="h-5 w-5 text-primary" />
              {t("clubs.nearby_modal.title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button variant="outline" className="w-full justify-start gap-3 h-auto py-3" onClick={() => handleLocationChoice('current')} data-testid="location-choice-current-btn">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Navigation className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">{t("clubs.nearby_modal.current_location")}</p>
                <p className="text-xs text-muted-foreground">GPS</p>
              </div>
            </Button>
            <Button variant="outline" className="w-full justify-start gap-3 h-auto py-3" onClick={() => handleLocationChoice('profile')} disabled={!user?.coordinates?.lat} data-testid="location-choice-profile-btn">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">{t("clubs.nearby_modal.profile_location")}</p>
                <p className="text-xs text-muted-foreground">
                  {user?.coordinates?.lat ? (user?.concelho ? `${user.concelho}, ${user.distrito}` : user?.distrito || 'Perfil') : t("clubs.nearby_modal.no_profile_coords")}
                </p>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search Modal */}
      <Dialog open={searchModalOpen} onOpenChange={(open) => { setSearchModalOpen(open); if (!open) resetSearchForm(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              {t("clubs.search_modal.title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Reference Point Label */}
            <p className="text-sm font-medium text-muted-foreground">{t("clubs.search_modal.reference_point")}</p>
            
            {/* Position Source Buttons */}
            <div className="flex gap-2">
              <Button variant={searchPositionSource === 'profile' ? 'default' : 'outline'} size="sm" onClick={() => handleSearchPositionSource('profile')} disabled={!userCoords} data-testid="search-position-profile-btn">
                <MapPin className="h-4 w-4 mr-1" /> {t("clubs.search_modal.profile")}
              </Button>
              <Button variant={searchPositionSource === 'gps' ? 'default' : 'outline'} size="sm" onClick={() => handleSearchPositionSource('gps')} disabled={searchGettingLocation} data-testid="search-position-gps-btn">
                {searchGettingLocation ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-1" /> : <Navigation className="h-4 w-4 mr-1" />}
                {t("clubs.search_modal.gps")}
              </Button>
            </div>
            {searchLocationName && <p className="text-sm text-muted-foreground">{searchLocationName}</p>}
            
            {/* Map */}
            <div className="h-64 rounded-lg overflow-hidden border border-border">
              <MapContainer center={searchMapCenter} zoom={8} className="h-full w-full" scrollWheelZoom={true}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                <SearchLocationMarker position={searchMapPosition} setPosition={(pos) => { setSearchMapPosition(pos); setSearchPositionSource('map'); setSearchLocationName('Mapa: Clique'); }} radius={searchRadius} />
                <MapRecenter center={searchMapCenter} />
              </MapContainer>
            </div>
            
            {/* Radius Slider */}
            <div>
              <label className="text-sm font-medium">{t("clubs.search_modal.radius")}: {searchRadius} km</label>
              <Slider value={[searchRadius]} onValueChange={([val]) => setSearchRadius(val)} min={sliderSettings?.clubs_slider?.min_km || 5} max={sliderSettings?.clubs_slider?.max_km || 50} step={1} className="mt-2" />
            </div>
            
            {/* Name Filter */}
            <div>
              <label className="text-sm font-medium">{t("clubs.search_modal.name_filter")}</label>
              <Input value={searchName} onChange={(e) => setSearchName(e.target.value.substring(0, 20))} placeholder={t("clubs.search_modal.name_placeholder")} maxLength={20} className="mt-1" />
            </div>
            
            {/* Search Button */}
            <Button onClick={handleSearch} disabled={!searchMapPosition || searchLoading} className="w-full" data-testid="search-clubs-submit-btn">
              {searchLoading ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              {t("clubs.search_modal.search")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Modal */}
      <Dialog open={mapModalOpen} onOpenChange={setMapModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="h-5 w-5 text-primary" />
              {t("clubs.map_modal.title")}
            </DialogTitle>
          </DialogHeader>
          <PortugalMap onSelect={handleMapSelect} groups={allGroups} />
        </DialogContent>
      </Dialog>

      {isAuthenticated && <NotificationLoginPopup />}
    </div>
  );
}

export { ClubsPage };
